/*
  THAANI Consent-Bruecke: PrivacyBee-Cookie-Banner -> Shopify Customer Privacy API

  Warum: Im Shopify-Admin steht der Kundendatenschutz fuer CH und DE auf
  "Einwilligung erforderlich". Shopify Analytics, die Web-Pixel und alle
  App-Pixel warten deshalb auf Shopify.customerPrivacy.setTrackingConsent().
  PrivacyBee ruft diese API nicht auf (0 Treffer in cookie-banner.js), also
  bleibt jeder Besuch fuer Shopify ein "awaiting consent". Dieses Skript reicht
  die im Banner getroffene Entscheidung an Shopify weiter.

  Belegte PrivacyBee-API (app.privacybee.io/cookie-banner.js, Stand 02.09.2026,
  Zeichen-Offsets in der heruntergeladenen Datei):
  - window.PrivacyBee (Offset 183033): getConsent(kat), getAllConsents(),
    hasExplicitConsent(), onConsentChange(cb), setAllConsents(obj), acceptAll(),
    rejectAll(). Instanz zusaetzlich unter window.privacybeeCookieBanner.
  - getAllConsents() liefert IMMER ein Objekt, auch solange der Banner offen ist
    (dann die Defaults). Nur hasExplicitConsent() sagt, ob der Besucher wirklich
    entschieden hat (liest das Cookie "privacybee_consent", explicitConsent).
  - Kategorien (Enum, Offset 115536): AD_STORAGE, ANALYTICS_STORAGE, AD_USER_DATA,
    AD_PERSONALIZATION, FUNCTIONALITY_STORAGE, PERSONALIZATION_STORAGE,
    SECURITY_STORAGE. Banner-Kategorien (Offset 115960): necessary ->
    SECURITY_STORAGE + FUNCTIONALITY_STORAGE (immer true), analytics ->
    ANALYTICS_STORAGE, marketing -> AD_STORAGE + AD_USER_DATA +
    AD_PERSONALIZATION + PERSONALIZATION_STORAGE.
  - Events auf document: "privacybee:ready" (Offset ~183420, detail ohne
    consents, feuert nachdem window.PrivacyBee existiert) und
    "privacybee:consent-changed" (Offset 118292, detail.consents mit den
    Kategorien oben, detail.explicitConsent). Beim Seitenstart ohne gespeicherte
    Einwilligung feuert consent-changed einmal mit explicitConsent:false
    (Defaults); das wird hier wie bei PrivacyBees eigenem GTM-Handler ignoriert.
  - Reload: Die erste Entscheidung laedt die Seite NICHT neu (Offset 175632),
    eine spaetere Aenderung ueber den Praeferenzen-Link schon. Deshalb wird
    beide Wege bedient: Event fuer die erste Entscheidung, gespeicherte
    Einwilligung beim Laden fuer alles danach.
  - Gespeicherte Einwilligung wird beim Laden nur in den Speicher uebernommen
    (restoreSettings, kein Event). Darum beim Start aktiv abfragen.

  Mapping auf Shopify (analytics, marketing, preferences, sale_of_data):
    analytics   = ANALYTICS_STORAGE
    marketing   = AD_STORAGE oder AD_USER_DATA oder AD_PERSONALIZATION
    preferences = PERSONALIZATION_STORAGE oder FUNCTIONALITY_STORAGE
                  (FUNCTIONALITY_STORAGE ist bei PrivacyBee Teil von
                  "necessary" und damit immer true, sobald entschieden wurde)
    sale_of_data = false (keine Weitergabe, CH und DE ohne CCPA-Bezug)

  Verhalten:
  - Solange keine explizite Einwilligung vorliegt (Banner offen), wird NICHTS
    an Shopify gemeldet; Shopify bleibt in "awaiting consent".
  - Shopify.loadFeatures kommt aus content_for_header, das im <head> nach dem
    Banner-Snippet steht. Das Skript wird deshalb mit defer geladen und wartet
    zusaetzlich (Polling, 10 s) auf beide Seiten.
  - Ohne PrivacyBee (Preview-URLs liefern 403) oder ohne Shopify-API passiert
    nichts, es gibt keine Konsolenfehler.
  - Diagnose: window.thaaniConsentDebug = true schaltet console.debug ein;
    window.thaaniConsentBridge.lastConsent zeigt das zuletzt gemeldete Objekt.
*/
(function () {
  'use strict';

  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.thaaniConsentBridge) return;

  var READY_TIMEOUT_MS = 10000;
  var POLL_MS = 250;
  var CATEGORY = {
    analytics: 'ANALYTICS_STORAGE',
    ad: 'AD_STORAGE',
    adUserData: 'AD_USER_DATA',
    adPersonalization: 'AD_PERSONALIZATION',
    personalization: 'PERSONALIZATION_STORAGE',
    functionality: 'FUNCTIONALITY_STORAGE'
  };

  var bridge = {
    version: '2026-09-03',
    lastConsent: null,
    mapConsents: mapConsents
  };
  window.thaaniConsentBridge = bridge;

  function debug() {
    if (!window.thaaniConsentDebug) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[thaani-consent]');
      (console.debug || console.log).apply(console, args);
    } catch (e) {
      /* Konsole nicht verfuegbar */
    }
  }

  function isTrue(consents, key) {
    return !!consents && consents[key] === true;
  }

  function mapConsents(consents) {
    var c = consents && typeof consents === 'object' ? consents : {};
    return {
      analytics: isTrue(c, CATEGORY.analytics),
      marketing:
        isTrue(c, CATEGORY.ad) || isTrue(c, CATEGORY.adUserData) || isTrue(c, CATEGORY.adPersonalization),
      preferences: isTrue(c, CATEGORY.personalization) || isTrue(c, CATEGORY.functionality),
      sale_of_data: false
    };
  }

  /* Pollt check() bis es einen Wert liefert oder die Zeit abgelaufen ist. */
  function waitFor(check, onReady, onTimeout) {
    var done = false;
    var started = Date.now();
    function attempt() {
      if (done) return;
      var value = null;
      try {
        value = check();
      } catch (e) {
        value = null;
      }
      if (value) {
        done = true;
        onReady(value);
        return;
      }
      if (Date.now() - started >= READY_TIMEOUT_MS) {
        done = true;
        if (onTimeout) onTimeout();
        return;
      }
      setTimeout(attempt, POLL_MS);
    }
    attempt();
    return function stop() {
      done = true;
    };
  }

  function getPrivacyBee() {
    var pb = window.PrivacyBee;
    if (!pb || typeof pb.getAllConsents !== 'function' || typeof pb.hasExplicitConsent !== 'function') {
      return null;
    }
    return pb;
  }

  function getShopifyApi() {
    var shopify = window.Shopify;
    var api = shopify && shopify.customerPrivacy;
    if (!api || typeof api.setTrackingConsent !== 'function') return null;
    return api;
  }

  /* Shopify-API bereitstellen: vorhanden nehmen, sonst per loadFeatures laden. */
  var apiQueue = [];
  var apiLoading = false;

  function flushApiQueue(api) {
    var queue = apiQueue;
    apiQueue = [];
    for (var i = 0; i < queue.length; i++) {
      try {
        queue[i](api);
      } catch (e) {
        debug('Fehler beim Anwenden', e);
      }
    }
  }

  function ensureShopifyApi(callback) {
    var api = getShopifyApi();
    if (api) {
      callback(api);
      return;
    }
    apiQueue.push(callback);
    if (apiLoading) return;
    apiLoading = true;

    var flushed = false;
    function onApi(readyApi) {
      if (flushed) return;
      flushed = true;
      debug('Shopify customerPrivacy bereit');
      flushApiQueue(readyApi);
    }

    waitFor(
      function () {
        var shopify = window.Shopify;
        return shopify && typeof shopify.loadFeatures === 'function' ? shopify : null;
      },
      function (shopify) {
        try {
          shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
            if (error) {
              debug('consent-tracking-api konnte nicht geladen werden', error);
              return;
            }
            var readyApi = getShopifyApi();
            if (readyApi) onApi(readyApi);
          });
        } catch (e) {
          debug('Shopify.loadFeatures hat einen Fehler geworfen', e);
        }
        /* Falls der Callback ausbleibt, die API aber (z. B. durch Trekkie) auftaucht. */
        waitFor(getShopifyApi, onApi, function () {
          if (flushed) return;
          /* Naechste Entscheidung darf es erneut versuchen. */
          apiLoading = false;
          debug('Shopify customerPrivacy nicht verfuegbar, Einwilligung nicht gemeldet');
        });
      },
      function () {
        apiLoading = false;
        debug('Shopify.loadFeatures nicht verfuegbar, Einwilligung nicht gemeldet');
      }
    );
  }

  var lastKey = null;

  function applyConsent(consent, source) {
    var key = JSON.stringify(consent);
    if (key === lastKey) {
      debug('unveraendert, kein erneuter Aufruf', source, consent);
      return;
    }
    lastKey = key;
    bridge.lastConsent = consent;
    debug('melde an Shopify', source, consent);
    ensureShopifyApi(function (api) {
      try {
        api.setTrackingConsent(consent, function (result) {
          var failed = result && (result instanceof Error || typeof result === 'string' || result.error);
          if (failed) {
            debug('setTrackingConsent meldet Fehler', result);
            return;
          }
          var current = null;
          try {
            current = typeof api.currentVisitorConsent === 'function' ? api.currentVisitorConsent() : null;
          } catch (e) {
            current = null;
          }
          debug('setTrackingConsent ok', consent, current);
        });
      } catch (e) {
        debug('setTrackingConsent hat einen Fehler geworfen', e);
      }
    });
  }

  var storedApplied = false;

  function applyStoredConsent(pb, source) {
    if (storedApplied) return;
    storedApplied = true;
    var explicit = false;
    try {
      explicit = pb.hasExplicitConsent() === true;
    } catch (e) {
      explicit = false;
    }
    if (!explicit) {
      debug('keine gespeicherte Einwilligung, Shopify bleibt bei awaiting consent', source);
      return;
    }
    var consents = null;
    try {
      consents = pb.getAllConsents();
    } catch (e) {
      consents = null;
    }
    if (!consents) {
      debug('getAllConsents lieferte nichts', source);
      return;
    }
    applyConsent(mapConsents(consents), source);
  }

  function onConsentChanged(event) {
    try {
      var detail = event && event.detail;
      if (!detail) return;
      if (detail.explicitConsent === false) {
        debug('consent-changed ohne explizite Entscheidung ignoriert');
        return;
      }
      var consents = detail.consents;
      if (!consents) {
        var pb = getPrivacyBee();
        consents = pb ? pb.getAllConsents() : null;
      }
      if (!consents) return;
      applyConsent(mapConsents(consents), 'privacybee:consent-changed');
    } catch (e) {
      debug('Fehler im consent-changed-Handler', e);
    }
  }

  function onReadyEvent() {
    try {
      var pb = getPrivacyBee();
      if (pb) applyStoredConsent(pb, 'privacybee:ready');
    } catch (e) {
      debug('Fehler im ready-Handler', e);
    }
  }

  try {
    document.addEventListener('privacybee:consent-changed', onConsentChanged, false);
    document.addEventListener('privacybee:ready', onReadyEvent, false);

    var pbNow = getPrivacyBee();
    if (pbNow) {
      applyStoredConsent(pbNow, 'bereits initialisiert');
    } else {
      waitFor(getPrivacyBee, function (pb) {
        applyStoredConsent(pb, 'polling');
      }, function () {
        debug('PrivacyBee nicht gefunden (Banner nicht geladen?), nichts zu tun');
      });
    }
  } catch (e) {
    debug('Bridge konnte nicht starten', e);
  }
})();
